import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { saveSession, loadSession, clearSession } from "@/lib/sessionCache";
import { get as getShiftOwner, set as setShiftOwner, clear as clearShiftOwner } from "@/lib/shiftOwner";
import { prewarmCache, initPrewarmOnReconnect } from "@/lib/offlinePrewarm";

const AuthContext = createContext(null);

// Auto-populate the shift-owner slot from the logged-in user IF they have
// an HR profile linked (employee_id). This satisfies the "el operador del
// turno sea el usuario que ingrese a la plataforma por defecto" request —
// supervisors can still swap it via the header chip when the tablet is
// handed to another miner mid-shift.
//
// If a shift owner from a PREVIOUS session is still stored (localStorage)
// and it does NOT match the current user's employee_id, we force-refresh
// so the header chip always reflects who is actually logged in. Before
// this fix, "MIGUEL OSWALDO GC" (owner set during previous supervisor's
// login) would linger after a different admin signed in on the same
// device.
async function _autoSetShiftOwnerFromUser(u) {
  try {
    if (!u) return;
    const currentOwner = getShiftOwner();
    if (!u.employee_id) {
      // Usuario sin ficha en Nómina (admin) --> si el owner previo pertenece
      // a alguien más, borralo. El supervisor lo re-asignará al recibir la
      // tablet mediante el chip.
      if (currentOwner) clearShiftOwner();
      return;
    }
    if (currentOwner && currentOwner.id === u.employee_id) return;   // ya coincide
    const { data } = await api.get(`/hr/employees/${u.employee_id}`).catch(() => ({ data: null }));
    const emp = data || { id: u.employee_id, name: u.name || u.email, position: "", crew: "" };
    setShiftOwner({ id: emp.id, name: emp.name, position: emp.position || "", crew: emp.crew || "" });
  } catch (_e) { /* silently ignore — not critical */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!alive) return;
        setUser(r.data);
        saveSession(r.data).catch(() => {});
        _autoSetShiftOwnerFromUser(r.data);
        // Pre-hidrata caché offline (Mi turno, HR, Mantenimiento, SGSSO).
        // Corre en background, no bloquea el login. También engancha el
        // event `online` para re-hidratar al recuperar la señal.
        prewarmCache(api).catch(() => {});
        initPrewarmOnReconnect(api);
      } catch (err) {
        const cached = await loadSession();
        if (!alive) return;
        const isNet = !err?.response;
        if (cached && isNet) {
          setUser({ ...cached.user, _offline: true, _offline_expires_at: cached.expires_at });
        } else {
          setUser(false);
        }
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (user && typeof user === "object") {
      saveSession(user).catch(() => {});
      _autoSetShiftOwnerFromUser(user);
      // Segunda vía para disparar prewarm: cuando Login.jsx setea el user
      // manualmente tras un POST /auth/login exitoso. Es idempotente
      // (tiene cooldown de 60s), así que no colisiona con el prewarm del
      // primer useEffect.
      if (!user._offline) {
        prewarmCache(api).catch(() => {});
        initPrewarmOnReconnect(api);
      }
    }
  }, [user]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("logout failed:", err?.message || err); }
    await clearSession();
    clearShiftOwner();   // limpia el operador del turno al cerrar sesion --
                          // evita que quede colgado del usuario anterior
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, setUser, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
